/**
 * Dev mode → Kiaan Browser 🔒 (SUN-101b). Admin / owner only (DevMode decides; the server says 403 to anyone else).
 * Live screen + take control · Teach a job · Jobs Kiaan knows (approve, edit, run, schedule).
 * Kiaan never types a password: the admin logs in by hand and the logins stay in this browser.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  approveJob, getJobSource, getOperatorScreenUrl, getOperatorStatus, getRun, getTeachCurrent, listJobRuns, listJobs, operatorErrorText,
  operatorFileUrl, releaseBrowserControl, runJob, saveJobDraft, setJobSchedule, startTeach, stopTeach, takeBrowserControl, undoTeachStep,
  type BrowserJob, type JobRunRow, type JobState, type OperatorRun, type OperatorStatus, type TeachCurrent,
} from '../kiaanApi';
import { DevState, useDevData } from './useDevData';

const JOB_PILL: Record<JobState, { label: string; cls: string }> = {
  draft: { label: 'waiting for your OK', cls: 'kw-wt' },
  approved: { label: 'approved', cls: 'kw-ok' },
  changed: { label: 'changed — approve again', cls: 'kw-bad' },
};
const NAME_OK = /^[a-z0-9][a-z0-9-]{0,59}$/;
const fmtDate = (iso: string) => new Date(iso).toLocaleString();

type Act = (fn: () => Promise<unknown>, ok?: string) => Promise<void>;

export function KiaanBrowser() {
  const { data, loading, error, reload } = useDevData<OperatorStatus>(getOperatorStatus);
  const [note, setNote] = useState<string | null>(null);
  const act: Act = async (fn, ok) => {
    setNote(null);
    try { await fn(); if (ok) setNote(ok); reload(); } catch (e) { setNote(operatorErrorText(e)); }
  };
  if (!data) return <DevState loading={loading} error={error} />;
  return (
    <div className="kw-grid" data-testid="browser-panel">
      {note && <div className="kw-banner" role="status">{note}</div>}
      <ScreenCard s={data} act={act} />
      <TeachCard act={act} />
      <JobsCard s={data} act={act} />
    </div>
  );
}

function ScreenCard({ s, act }: { s: OperatorStatus; act: Act }) {
  const [src, setSrc] = useState<string | null>(null);
  const [driving, setDriving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const open = async (viewOnly: boolean) => { setSrc(await getOperatorScreenUrl(viewOnly)); };
  const watch = async () => { setErr(null); try { await open(true); } catch (e) { setErr(operatorErrorText(e)); } };
  const take = () => act(async () => { await takeBrowserControl(); await open(false); setDriving(true); });
  const release = () => act(async () => {
    const r = await releaseBrowserControl();
    setDriving(false);
    await open(true);
    const sites = r?.login?.sites ?? [];
    setErr(sites.length ? `Saved logins: ${sites.join(', ')}` : null);
  });
  const by = s.login.by?.name;
  const heldByOther = !!s.control.heldBy && !driving;
  return (
    <div className="kw-box" style={{ gridColumn: '1 / -1' }} data-tour="kb-screen">
      <h4>Live screen</h4>
      <div className="kw-row" data-testid="browser-status">
        {s.session.open ? '● Browser on' : '○ Browser off'}
        {' · '}
        {by ? `Logged in here by ${by}: ${s.login.sites.join(', ') || 'no sites yet'}` : 'No login saved yet'}
        {s.control.heldBy && <span className="kw-small"> · {s.control.heldBy.name} is driving</span>}
      </div>
      <div className="kw-row">
        {!src && <button type="button" className="kw-btn kw-light" onClick={watch}>Watch live</button>}
        {!driving && <button type="button" className="kw-btn" disabled={heldByOther} onClick={take}>Take control</button>}
        {driving && <button type="button" className="kw-btn" onClick={release}>Hand back</button>}
        {src && !driving && <button type="button" className="kw-btn kw-light" onClick={() => setSrc(null)}>Hide</button>}
      </div>
      {err && <div className="kw-small">{err}</div>}
      {src && <iframe title="Kiaan Browser screen" src={src} style={{ width: '100%', height: 420, border: '1px solid var(--line, #ddd)' }} />}
      <div className="kw-small">Admin drives with mouse and keyboard in this window. Kiaan never types a password; your logins stay in this browser.</div>
    </div>
  );
}

function TeachCard({ act }: { act: Act }) {
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [url, setUrl] = useState('https://');
  const [cur, setCur] = useState<TeachCurrent | null>(null);
  const [sent, setSent] = useState<string | null>(null);
  const refresh = useCallback(() => getTeachCurrent().then(setCur).catch(() => undefined), []);
  useEffect(() => { refresh(); }, [refresh]);
  const recording = !!cur?.recording;
  useEffect(() => {
    if (!recording) return undefined;
    const t = setInterval(refresh, 2000);
    return () => clearInterval(t);
  }, [recording, refresh]);
  const kept = (cur?.steps ?? []).filter((x) => !x.dropped).length;
  const valid = NAME_OK.test(name) && /^https:\/\/\S+$/.test(url) && desc.trim().length > 0;
  return (
    <div className="kw-box" data-tour="kb-teach">
      <h4>Teach a job</h4>
      {!recording ? (
        <>
          <div className="kw-row"><input aria-label="Job name" placeholder="job name (lowercase, digits, dashes)" value={name} onChange={(e) => setName(e.target.value.toLowerCase())} /></div>
          <div className="kw-row"><input aria-label="What the job does" placeholder="one line: what it does" maxLength={200} value={desc} onChange={(e) => setDesc(e.target.value)} style={{ width: '100%' }} /></div>
          <div className="kw-row"><input aria-label="Start address" value={url} onChange={(e) => setUrl(e.target.value)} style={{ width: '100%' }} /></div>
          <div className="kw-row">
            <button type="button" className="kw-btn" disabled={!valid}
              onClick={() => act(async () => { setSent(null); await startTeach(name, desc.trim(), url); await refresh(); }, 'Recording. Take control and do the job by hand.')}>Start</button>
          </div>
          {sent && <div className="kw-row">{sent}</div>}
        </>
      ) : (
        <>
          <div className="kw-row">● Watching you · {kept} steps</div>
          <ol data-testid="teach-steps" style={{ margin: '4px 0', paddingLeft: 20 }}>
            {cur!.steps.map((st) => (
              <li key={st.n} style={st.dropped ? { textDecoration: 'line-through', opacity: 0.6 } : undefined}>{st.text}</li>
            ))}
          </ol>
          <div className="kw-row">
            <button type="button" className="kw-btn" onClick={() => act(async () => {
              const r = await stopTeach();
              setSent(`Sent to Kiaan Browser: ${r.identifier ?? r.taskId}`);
              setName(''); setDesc('');
              await refresh();
            })}>■ Stop &amp; save job</button>
            <button type="button" className="kw-btn kw-light" disabled={kept === 0} onClick={() => act(async () => { await undoTeachStep(); await refresh(); })}>Undo last step</button>
          </div>
        </>
      )}
    </div>
  );
}

function JobsCard({ s, act }: { s: OperatorStatus; act: Act }) {
  const jobs = useDevData<BrowserJob[]>(listJobs);
  const actAndReload: Act = (fn, ok) => act(async () => { await fn(); jobs.reload(); }, ok);
  return (
    <div className="kw-box" style={{ gridColumn: '1 / -1' }} data-tour="kb-jobs">
      <h4>Jobs Kiaan knows <button type="button" className="kw-btn kw-light" onClick={jobs.reload}>Refresh</button></h4>
      {!jobs.data && <DevState loading={jobs.loading} error={jobs.error} />}
      {jobs.data && jobs.data.length === 0 && <div className="kw-small">No jobs yet. Teach one above.</div>}
      {(jobs.data ?? []).map((j) => <JobRow key={j.name} job={j} busy={!!s.busy} act={actAndReload} />)}
    </div>
  );
}

function JobRow({ job, busy, act }: { job: BrowserJob; busy: boolean; act: Act }) {
  const realInputs = job.inputs.filter((i) => i !== 'baseUrl');
  const [editing, setEditing] = useState(false);
  const [source, setSource] = useState('');
  const [inputs, setInputs] = useState<Record<string, string>>({});
  const [time, setTime] = useState(job.schedule?.time ?? '');
  const [schedInputs, setSchedInputs] = useState<Record<string, string>>(job.schedule?.inputs ?? {});
  const [runId, setRunId] = useState<string | null>(null);
  const [openRun, setOpenRun] = useState<string | null>(null);
  const [runs, setRuns] = useState<JobRunRow[]>([]);
  useEffect(() => {
    if (job.state === 'approved') listJobRuns(job.name).then(setRuns).catch(() => undefined);
  }, [job.name, job.state, job.runs.count]);
  const pill = JOB_PILL[job.state];
  const last = job.runs.last;
  const inputBoxes = (vals: Record<string, string>, set: (v: Record<string, string>) => void, prefix: string) =>
    realInputs.map((i) => (
      <input key={i} aria-label={`${prefix} ${i}`} placeholder={i} value={vals[i] ?? ''} onChange={(e) => set({ ...vals, [i]: e.target.value })} />
    ));
  const edit = () => act(async () => {
    setSource(await getJobSource(job.name, job.hasDraft ? 'draft' : 'approved'));
    setEditing(true);
  });
  return (
    <div className="kw-box" data-testid={`job-${job.name}`}>
      <div className="kw-row" style={{ flexWrap: 'wrap', gap: 6 }}>
        <b>{job.name}</b>
        <span className={`kw-pill ${pill.cls}`}>{pill.label}</span>
        {job.description && <span className="kw-small">{job.description}</span>}
      </div>
      <div className="kw-row kw-small">
        Inputs: {realInputs.length ? realInputs.join(', ') : 'none'}
        {job.state === 'approved' && <> · runs: {job.runs.count}{last ? ` · last ${fmtDate(last.at)} ${last.ok ? '✓' : '✗'}` : ''}</>}
        {job.state === 'approved' && job.schedule?.time && <> · daily at {job.schedule.time}</>}
      </div>
      <div className="kw-row" style={{ flexWrap: 'wrap', gap: 6 }}>
        {job.state !== 'approved' && <button type="button" className="kw-btn" onClick={() => act(() => approveJob(job.name), `Approved ${job.name}.`)}>Approve</button>}
        <button type="button" className="kw-btn kw-light" onClick={edit}>Edit</button>
      </div>
      {editing && (
        <div>
          <textarea aria-label={`${job.name} source`} value={source} onChange={(e) => setSource(e.target.value)} rows={14} style={{ width: '100%', fontFamily: 'monospace' }} />
          <div className="kw-row">
            <button type="button" className="kw-btn" onClick={() => act(async () => { await saveJobDraft(job.name, source); setEditing(false); }, 'Saved as a draft. Approve it to use it.')}>Save</button>
            <button type="button" className="kw-btn kw-light" onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </div>
      )}
      {job.state === 'approved' && (
        <>
          <div className="kw-row" style={{ flexWrap: 'wrap', gap: 6 }}>
            {inputBoxes(inputs, setInputs, `${job.name} run`)}
            <button type="button" className="kw-btn" disabled={busy}
              onClick={() => act(async () => { setRunId((await runJob(job.name, inputs)).runId); }, 'Running…')}>Run now</button>
          </div>
          <div className="kw-row" style={{ flexWrap: 'wrap', gap: 6 }}>
            Every day at <input type="time" aria-label={`${job.name} time`} value={time} onChange={(e) => setTime(e.target.value)} />
            {inputBoxes(schedInputs, setSchedInputs, `${job.name} schedule`)}
            <button type="button" className="kw-btn kw-light" disabled={!time} onClick={() => act(() => setJobSchedule(job.name, time, schedInputs), 'Schedule saved.')}>Schedule</button>
            {job.schedule?.time && <button type="button" className="kw-btn kw-light" onClick={() => act(async () => { await setJobSchedule(job.name, null, {}); setTime(''); setSchedInputs({}); }, 'Schedule cleared.')}>Clear</button>}
          </div>
          {runs.map((r) => (
            <div key={r.runId} className="kw-row">
              <button type="button" className="kw-small" style={{ background: 'none', border: 0, cursor: 'pointer' }} onClick={() => setOpenRun(openRun === r.runId ? null : r.runId)}>{fmtDate(r.at)}</button>
              <span className={`kw-pill kw-ml ${r.ok ? 'kw-ok' : 'kw-bad'}`}>{r.ok ? 'passed' : 'failed'}</span>
              <span className="kw-small">{r.trigger}</span>
            </div>
          ))}
          {openRun && <RunView runId={openRun} />}
          {runId && <RunView runId={runId} />}
        </>
      )}
    </div>
  );
}

/** Steps, pass/fail, the failed-step screenshot, video and trace — all through the backend. Polls while it runs. */
export function RunView({ runId }: { runId: string }) {
  const [run, setRun] = useState<OperatorRun | null>(null);
  const load = useCallback(() => getRun(runId).then(setRun).catch(() => undefined), [runId]);
  useEffect(() => {
    load();
    const t = setInterval(() => { if (!run?.result || run.running) load(); }, 3000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId, run?.running, !!run?.result]);
  if (!run) return <div className="kw-small">Loading…</div>;
  const files = run.files ?? [];
  const failed = files.find((f) => /^failed-step-\d+\.png$/.test(f.name));
  const video = files.find((f) => f.name.endsWith('.webm'));
  const trace = files.find((f) => f.name === 'trace.zip');
  return (
    <div className="kw-box" data-testid="run-view">
      <h4>{runId} <span className={`kw-pill ${run.result ? (run.result.ok ? 'kw-ok' : 'kw-bad') : 'kw-wip'}`}>{run.result ? (run.result.ok ? 'passed' : 'failed') : 'running'}</span></h4>
      {(run.result?.steps ?? []).map((st) => (
        <div key={st.n} className="kw-row">{st.ok ? '✓' : '✗'} {st.n}. {st.name}{st.error ? <span className="kw-small"> — {st.error.slice(0, 160)}</span> : null}</div>
      ))}
      {run.result?.error && <div className="kw-small">{run.result.error}</div>}
      {failed && <img alt="Screenshot of the failed step" src={operatorFileUrl(runId, failed.name)} style={{ maxWidth: '100%', border: '1px solid var(--line, #ddd)' }} />}
      <div className="kw-row">
        {video && <a href={operatorFileUrl(runId, video.name)} target="_blank" rel="noreferrer">Video</a>}
        {trace && <a href={operatorFileUrl(runId, trace.name)} target="_blank" rel="noreferrer" style={{ marginLeft: 8 }}>Trace</a>}
      </div>
    </div>
  );
}
